import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  Cpu,
  CreditCard,
  Play,
  Server,
  Package
} from 'lucide-react';
import ComputeWorldMap from './ComputeWorldMap';
import * as clusterService from '../services/clusterService';
import * as imageService from '../services/imageService';
import * as orchestratorService from '../services/orchestratorService';
import { useAuth } from '../hooks/useAuth';

const Dashboard = () => {
  const { user } = useAuth();
  const [clusters, setClusters] = useState([]);
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [costLoading, setCostLoading] = useState(true);
  const [curatedDockerImages, setCuratedDockerImages] = useState(new Set());

  useEffect(() => {
    fetchClusters();
    fetchCostData();
    imageService.listCuratedImages().then(data => {
      if (data.success && data.images) {
        setCuratedDockerImages(new Set(data.images.map(img => img.docker_image)));
      }
    }).catch(() => {});
  }, []);

  const fetchClusters = async () => {
    try {
      setLoading(true);
      const response = await clusterService.listClusters();
      if (response.success) {
        setClusters(response.clusters || []);
      }
    } catch (err) {
      console.error('Failed to fetch clusters:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCostData = async () => {
    try {
      setCostLoading(true);
      const response = await orchestratorService.getCostReport(1);
      if (response.success) {
        setCostData(response.costData || []);
      }
    } catch (err) {
      // 403 = non-admin user, silently skip cost data
      if (err?.status !== 403) {
        console.error('Failed to fetch cost data:', err);
      }
    } finally {
      setCostLoading(false);
    }
  };

  const activeInstances = clusters.filter(c => c.status === 'UP').length;
  const totalInstances = clusters.length;

  // Compute cost today from cost report
  const costToday = costData && Array.isArray(costData)
    ? costData.reduce((sum, c) => sum + (c.total_cost || 0), 0)
    : null;

  // Compute per-cloud cost breakdown
  const costByCloud = {};
  if (costData && Array.isArray(costData)) {
    costData.forEach(c => {
      const cloud = c.cloud || 'Other';
      costByCloud[cloud] = (costByCloud[cloud] || 0) + (c.total_cost || 0);
    });
  }
  const cloudCosts = Object.entries(costByCloud).sort((a, b) => b[1] - a[1]);
  const totalCost = cloudCosts.reduce((sum, [, cost]) => sum + cost, 0);

  const cloudColors = {
    'aws': { ring: '#8b5cf6', dot: 'bg-purple-600', label: 'AWS' },
    'gcp': { ring: '#3b82f6', dot: 'bg-blue-600', label: 'GCP' },
    'azure': { ring: '#10b981', dot: 'bg-green-600', label: 'Azure' },
    'runpod': { ring: '#f59e0b', dot: 'bg-yellow-500', label: 'RunPod' },
    'vast': { ring: '#ef4444', dot: 'bg-red-500', label: 'Vast.ai' },
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Instances</p>
              <p className="text-2xl font-bold text-gray-900">
                {loading ? '...' : `${activeInstances}/${totalInstances}`}
              </p>
            </div>
            <Cpu className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-400">--</p>
            </div>
            <BarChart3 className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Jobs 24h</p>
              <p className="text-2xl font-bold text-gray-400">--</p>
            </div>
            <Play className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cost Today</p>
              <p className="text-2xl font-bold text-gray-900">
                {costLoading ? '...' : costToday !== null ? `$${costToday.toFixed(2)}` : '--'}
              </p>
            </div>
            <CreditCard className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4">Current Usage</h3>
          {costLoading ? (
            <div className="flex justify-center items-center py-12">
              <span className="text-gray-500">Loading cost data...</span>
            </div>
          ) : cloudCosts.length > 0 ? (
            <>
              <div className="relative w-48 h-48 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                  {cloudCosts.slice(0, 3).map(([cloud], i) => {
                    const radius = 45 - (i * 10);
                    const circumference = 2 * Math.PI * radius;
                    const ratio = totalCost > 0 ? (costByCloud[cloud] / totalCost) : 0;
                    const offset = circumference * (1 - ratio);
                    const color = cloudColors[cloud]?.ring || '#6b7280';
                    return (
                      <circle key={cloud} cx="50" cy="50" r={radius}
                        stroke={color} strokeWidth={8 - i * 2} fill="none"
                        strokeDasharray={circumference} strokeDashoffset={offset}
                        className="transition-all duration-300" />
                    );
                  })}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold">${totalCost.toFixed(2)}</div>
                    <div className="text-sm text-gray-500">today</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {cloudCosts.map(([cloud, cost]) => (
                  <div key={cloud} className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 ${cloudColors[cloud]?.dot || 'bg-gray-500'} rounded-full`}></div>
                      <span className="text-sm">{cloudColors[cloud]?.label || cloud}</span>
                    </div>
                    <span className="text-sm font-medium">${cost.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <CreditCard size={32} className="mx-auto mb-2 text-gray-400" />
              <p>No cost data available</p>
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4">Active Clusters</h3>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : clusters.length > 0 ? (
            <div className="space-y-3">
              {clusters.slice(0, 5).map(cluster => (
                <div key={cluster.id || cluster.cluster_name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <div className="font-medium">{cluster.cluster_name}</div>
                      {cluster.image_id && curatedDockerImages.has(cluster.image_id) && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-300 flex items-center space-x-0.5">
                          <Package size={8} />
                          <span>Curated</span>
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {cluster.cloud_provider?.toUpperCase() || 'Cloud'} {cluster.region && `• ${cluster.region}`}
                    </div>
                    {cluster.owner_email && cluster.user_id !== user?.id && (
                      <div className="text-xs text-gray-400">Owner: {cluster.owner_email}</div>
                    )}
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    cluster.status === 'UP' ? 'bg-green-100 text-green-800' :
                    cluster.status === 'INIT' ? 'bg-yellow-100 text-yellow-800' :
                    cluster.status === 'STOPPED' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {cluster.status || 'UNKNOWN'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Server size={32} className="mx-auto mb-2 text-gray-400" />
              <p>No clusters running</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Compute Locations</h3>
        <ComputeWorldMap clusters={clusters} />
      </div>
    </div>
  );
};

export default Dashboard;
